// ─────────────────────────────────────────────────────────────────────────────
// compose.ts — the V2 Writer page's renderVals(), ported. Writer.dc.html
// (Rexy MEDIA mission control V2.zip, 2 Nov 2026) builds every card from
// sample data; here every card is built from the BOARD (his T2, composed by
// server/media_sawyer.py). The card kit (rows · items · bars · meter · vision ·
// doc), the titles, the tags, the colours and the source lines are the design's.
//
// NOTHING IS INVENTED. An empty folder is an empty card that says what fills
// it and when; a value the backend does not hold is not shown.
// ─────────────────────────────────────────────────────────────────────────────

import type { Board, DocBlock, QueueRow, ScriptView, KickBack } from './sawyerApi';

// The design's colours (Writer.dc.html renderVals).
export const G = '#5fd38a', R = '#eb8a7f', A = '#e6982f', B = '#9fd6f7', W = '#e8eef0', M = 'rgba(220,228,230,0.62)';
export const T1c = '#f4f6f7', T2c = B, DEc = G, SVc = '#f2b35c', SHc = 'rgba(215,168,120,0.95)';

export const TABS = ['Home', 'Today', 'Season Plan', 'Outlines', 'Scripts', 'Journeys', 'Kick-backs & Gaps', 'Expertise', 'Gate'] as const;
export const TAB = { home: 0, today: 1, kick: 6, gate: 8 };

export interface Row { k: string; v: string; c: string }
export interface Item {
  t: string; s: string; tag: string; tc: string; meta: string; hasMeta: boolean;
  link: string; hasLink: boolean; go: (() => void) | null;
  thread: { who: string; text: string; mine: boolean }[]; hasThread: boolean;
}
export interface Bar { t: string; v: string; vc: string; pct: number; color: string }
export interface DocLine { isHead: boolean; isAction: boolean; isLine: boolean; label: string; tc: string; text: string; who: string; cc: string }
export interface Card {
  lbl: string; aside: string; src: string; wide: boolean;
  rows?: Row[]; items?: Item[]; bars?: Bar[];
  meter?: { mv: string; mmax: string; pct: number };
  vision?: true; doc?: DocLine[];
}

export interface Hooks {
  go: (tab: number, extra?: { sel?: string; openKick?: string }) => void;
  openKick: Record<string, boolean>;
  toggleKick: (id: string) => void;
}

// ── small helpers ────────────────────────────────────────────────────────────
export function since(iso?: string | null, now = Date.now()): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const m = Math.max(0, Math.round((now - t) / 60000));
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h` : `${Math.round(h / 24)} d`;
}

export function clockOf(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function runtime(s: unknown): string {
  return s === null || s === undefined || s === '' ? '' : `${s} s`;
}

export function linesOf(l: QueueRow['lines']): string {
  if (!l || typeof l !== 'object') return '';
  const parts: string[] = [];
  if (typeof l.a === 'number') parts.push(`${l.a} A lines`);
  if (typeof l.b === 'number') parts.push(`${l.b} B lines`);
  return parts.join(' · ');
}

const item = (o: Partial<Item> & { t: string }): Item => ({
  t: o.t, s: o.s || '', tag: o.tag || '', tc: o.tc || B, meta: o.meta || '', hasMeta: !!o.meta,
  link: o.link || '', hasLink: !!o.link, go: o.go || null, thread: o.thread || [], hasThread: !!(o.thread && o.thread.length),
});
const card = (lbl: string, o: Partial<Card>): Card => ({ lbl, aside: '', src: '', wide: false, ...o });

export function tagOfState(state?: string): [string, string] {
  if (state === 'passed') return ['PASSED', G];
  if (state === 'kicked_back') return ['KICKED BACK', R];
  if (state === 'at_gate') return ['AT GATE', A];
  return ['WRITING', B];
}

export function docLines(doc: DocBlock[]): DocLine[] {
  return (doc || []).map((b) => {
    const isHead = b.kind === 'head', isAction = b.kind === 'action', isLine = b.kind === 'line_a' || b.kind === 'line_b';
    const speaker = (b.speaker || '').trim().toUpperCase();
    const mark = b.kind === 'line_a' ? 'A · INNER VOICE' : b.kind === 'line_b' ? 'B · TALKING' : '';
    return {
      isHead, isAction, isLine,
      label: isHead ? String(b.label || '') : '', tc: isHead ? String(b.timecode || '') : '',
      text: String(b.text || ''),
      who: isLine ? (speaker ? `${speaker} · ${mark}` : mark) : '',
      cc: b.kind === 'line_a' ? B : '#f2b35c',
    };
  });
}

function scriptOf(board: Board, eid: string): ScriptView | undefined {
  return board.scripts.find((s) => s.episode_id === eid);
}

function kickId(k: KickBack) {
  return `${k.episode_id}:v${k.version_kicked ?? ''}`;
}

function kickRows(board: Board) {
  return board.kickbacks.map((k) => {
    const open = k.state !== 'fixed';
    const lastMatt = (k.chat?.messages || []).filter((m) => m.who === 'Matt').slice(-1)[0];
    const state = k.state === 'fixed'
      ? `chat saved · v${k.redo_version ?? '?'} submitted · v${k.version_kicked ?? '?'} deleted`
      : k.state === 'redoing' ? `chat saved · v${(k.version_kicked ?? 0) + 1} being written · v${k.version_kicked ?? '?'} deleted when it is submitted`
      : k.state === 'chat_saved' ? 'chat saved · Sawyer redoes it next lap'
      : 'open the gate chat to give the direction';
    return {
      id: kickId(k), t: `${k.episode_id} · script v${k.version_kicked ?? '?'}`,
      dir: k.direction ? k.direction : lastMatt ? `Matt: "${lastMatt.text}"` : 'Matt: direction in the gate chat',
      tag: open ? 'OPEN' : 'FIXED', tc: open ? R : G, state, kind: k.kind || '',
      chat: (k.chat?.messages || []).map((m) => ({ who: m.who, text: m.text, mine: m.who === 'Matt' })),
    };
  });
}

export function gapBars(board: Board): Bar[] {
  const at = board.gap_at || 10;
  return board.gaps.map((g) => {
    const n = g.count;
    const color = n >= at ? R : n >= at / 2 ? A : '#5fb8f0';
    return { t: g.kind, v: `${n} of ${at}`, vc: n >= at ? R : n >= at / 2 ? A : W, pct: Math.min(100, Math.round((n / at) * 100)), color };
  });
}

function progressRows(board: Board): Row[] {
  const sp = board.season_plan;
  if (!sp.present) return [{ k: 'No season plan yet', v: 'written from your season vision at the season start', c: M }];
  return sp.progress.map((p) => {
    const eps = p.episodes || [];
    const range = eps.length === 0 ? '' : eps.length === 1 ? eps[0].replace(/^S\d+/, '') : `${eps[0].replace(/^S\d+/, '')}–${eps[eps.length - 1].replace(/^S\d+/, '')}`;
    const isFinale = eps.includes(sp.finale_episode_id || '');
    const k = [p.step, p.name, range + (isFinale ? ' · finale' : '')].filter((x) => x !== undefined && x !== '').join(' · ');
    const t = p.tally || {};
    const parts: string[] = [];
    if (t['passed']) parts.push(`${t['passed']} passed`);
    if (t['at gate']) parts.push(`${t['at gate']} at gate`);
    if (t['kicked back']) parts.push(`${t['kicked back']} kicked back`);
    if (t['writing']) parts.push(`${t['writing']} writing`);
    if (t['drafting']) parts.push(`${t['drafting']} drafting`);
    const allPassed = eps.length > 0 && (t['passed'] || 0) === eps.length;
    return { k, v: parts.length ? parts.join(' · ') : 'not started', c: allPassed ? G : parts.length ? A : M };
  });
}

function inFlightItems(board: Board): Item[] {
  const out: Item[] = [];
  for (const s of board.scripts) {
    if (s.state === 'written') out.push(item({ t: `${s.episode_id} · script v${s.version ?? 1}`, s: 'written · going to Gate 1', tag: 'WRITING', tc: B }));
  }
  const step = board.status.step_now;
  if (step && step.file && step.episode_id && !out.length) {
    out.push(item({ t: `${step.episode_id} · ${step.file.replace(/\.md$/, '')}`, s: 'Sawyer is on this step now', tag: 'WRITING', tc: B }));
  }
  for (const o of board.outlines) {
    if (!o.state || o.state === 'drafting') {
      out.push(item({ t: `${o.episode_id} · outline`, s: [o.encounter, o.reversal, o.cliffhanger_type].filter(Boolean).join(' · ') || o.summary || '', tag: 'DRAFTING', tc: A }));
    }
  }
  return out.length ? out : [item({ t: 'Nothing in flight', s: 'the loop writes the next episode when LOOP is on', tag: '', tc: M })];
}

function journeysHome(board: Board): Item[] {
  const out: Item[] = [];
  const sid = board.season_id;
  const written = board.journeys.season_summaries;
  for (const s of written) out.push(item({ t: `Season ${String(s.season_id || '').replace(/^S0?/, '')} summary`, s: String(s.summary || '').slice(0, 140), tag: s.season_id || '', tc: B }));
  if (sid && !written.some((s) => s.season_id === sid)) out.push(item({ t: `Season ${sid.replace(/^S0?/, '')} summary`, s: 'in progress — written after the finale', tag: sid, tc: M }));
  for (const c of board.journeys.characters.slice(0, 2)) out.push(item({ t: String(c.name || c.subject || 'Character'), s: String(c.latest_update || c.arc || ''), tag: 'LATEST', tc: B }));
  for (const r of board.journeys.relationships.slice(0, 1)) out.push(item({ t: String(r.name || r.subject || 'Relationship'), s: String(r.kind || r.summary || ''), tag: String(r.since || ''), tc: B }));
  return out.length ? out : [item({ t: 'No journeys yet', s: 'season summaries are written once a finale passes; character files are Hammond\'s, later', tc: M })];
}

export function statusLine(board: Board | null, loopOn: boolean | null): string {
  if (!board) return 'UNREAD';
  const st = board.status;
  const word = st.paused ? 'PAUSED · GATE CHAT' : st.walking ? 'LOOPING' : loopOn ? 'LOOP ON · RESTING' : loopOn === false ? 'LOOP OFF' : 'LOOP —';
  const step = st.step_now && st.step_now.episode_id ? ` · ${st.step_now.episode_id} ${String(st.step_now.file || '').replace(/\.md$/, '').toUpperCase()}` : '';
  return `${word}${step} · ${board.gate.waiting.length} AT GATE 1`;
}

export function ticker(board: Board | null): string[] {
  if (!board) return [];
  const out: string[] = [];
  const st = board.status;
  if (st.step_now && st.step_now.episode_id) out.push(`${st.step_now.episode_id} ${String(st.step_now.file || '').replace(/\.md$/, '').toLowerCase()} · writing`);
  for (const q of board.gate.waiting) out.push(`${q.episode_id} at Gate 1 · ${since(q.dropped_at)}`);
  for (const g of board.gaps.slice(0, 2)) out.push(`gap: ${g.kind} · ${g.count} of ${g.at}`);
  if (st.last_error) out.push(`stopped: ${st.last_error}`);
  return out;
}

// ── the tabs ────────────────────────────────────────────────────────────────
export function homeCards(board: Board, loopOn: boolean | null, h: Hooks): Card[] {
  const waiting = board.gate.waiting;
  const st = board.status;
  const loopState: [string, string] = st.paused ? ['paused · gate chat open', A] : loopOn ? ['armed', G] : loopOn === false ? ['off', M] : ['unread', M];
  const step = st.step_now && st.step_now.file ? `${String(st.step_now.file).replace(/\.md$/, '')}${st.step_now.episode_id ? ' · ' + st.step_now.episode_id : ''}` : (st.walking ? 'between steps' : '—');
  const lap = st.last_lap && st.last_lap.seconds !== undefined ? `${Math.round((st.last_lap.seconds || 0) / 60)} min${st.last_lap.episode_id ? ' · ' + st.last_lap.episode_id : ''}` : '—';
  const kicks = kickRows(board);
  const loaded = board.expertise.filter((e) => e.state === 'loaded').length;
  const ph = board.expertise.filter((e) => e.state === 'placeholder').length;
  const missing = board.expertise.length - loaded - ph;
  return [
    card('At Gate 1', {
      aside: `${waiting.length} waiting`, src: 'from Gate',
      items: waiting.length ? waiting.map((q) => item({ t: `${q.episode_id} · "${q.title || ''}"`, s: `v${q.version ?? '?'} · waiting ${since(q.dropped_at)}`, tag: 'AT GATE', tc: A, link: 'Open in Gate', go: () => h.go(TAB.gate, { sel: q.episode_id }) }))
        : [item({ t: 'Gate clear', s: 'nothing waiting for Matt', tc: B })],
    }),
    card('Loop', { src: 'from Today', rows: [{ k: 'State', v: loopState[0], c: loopState[1] }, { k: 'Step now', v: step, c: W }, { k: 'Last lap', v: lap, c: W }] }),
    card('Season vision', { wide: true, aside: "Matt's text · read-only", src: 'Season vision · T2 · edited only by Matt from here · Sawyer reads it', vision: true }),
    card('Season progress', { aside: board.season_id || '', src: 'from Season Plan', rows: progressRows(board) }),
    card('In flight', { src: 'from Outlines and Scripts', items: inFlightItems(board) }),
    card('Journeys', { src: 'from Journeys', items: journeysHome(board) }),
    card('Kick-backs & gaps', {
      src: 'from Kick-backs & Gaps',
      items: kicks.length ? kicks.map((k) => item({ t: k.t, s: k.dir, tag: k.tag, tc: k.tc, meta: k.state, link: 'Open chat', go: () => h.go(TAB.kick, { openKick: k.id }) }))
        : [item({ t: 'No kick-backs', s: 'a script Matt kicks back shows here with its chat', tc: M })],
      bars: gapBars(board),
    }),
    card('Expertise', { src: 'from Expertise', meter: { mv: String(loaded), mmax: `of ${board.expertise.length} loaded · ${ph} placeholders${missing ? ` · ${missing} not uploaded` : ''}`, pct: board.expertise.length ? Math.round((loaded / board.expertise.length) * 100) : 0 } }),
  ];
}

export function todayCards(board: Board): Card[] {
  const now = new Date();
  const aside = now.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  const rows: Item[] = [];
  for (const s of board.scripts) {
    if (s.state === 'written') rows.push(item({ t: `Script ${s.episode_id} · v${s.version ?? 1}`, s: 'written · going to Gate 1', tag: 'WRITING', tc: B }));
  }
  for (const q of board.today) {
    const [tag, tc] = tagOfState(q.state);
    const where = q.state === 'passed' ? 'handed to Marty' : q.state === 'kicked_back' ? `kicked back · v${(q.version ?? 0) + 1} next` : `at Gate 1 · ${since(q.dropped_at)}`;
    rows.push(item({ t: `Script ${q.episode_id} · v${q.version ?? '?'}`, s: [runtime(q.runtime_s), linesOf(q.lines), where].filter(Boolean).join(' · '), tag, tc }));
  }
  const drawing = board.status.drawing_on || [];
  const exp = new Map(board.expertise.map((e) => [e.file, e.state]));
  const step = board.status.step_now;
  const drawItems: Item[] = [];
  if (step && step.file) drawItems.push(item({ t: String(step.file).replace(/\.md$/, ''), s: 'the T1 step he is on' + (step.episode_id ? ` · ${step.episode_id}` : ''), tag: 'T1', tc: T1c }));
  for (const d of drawing) {
    const file = d.address.split('/').slice(-1)[0];
    const kind = d.kind;
    const [tag, tc] = kind === 'deep expertise' ? ['DEEP EXPERTISE', DEc] : kind === 'season vision' ? ['SEASON VISION', SVc]
      : kind.startsWith('domain') ? ['SHARED', SHc] : kind === 'T1' ? ['T1', T1c] : ['T2', T2c];
    const state = kind === 'deep expertise' ? (exp.get(file) || (d.present ? 'loaded' : 'not uploaded')) : d.present ? 'read' : 'nothing stored here yet';
    drawItems.push(item({ t: d.address, s: state, tag, tc }));
  }
  return [
    card("Today's queue", { aside, src: 'composed from: Sawyer T2 · gate states', items: rows.length ? rows : [item({ t: 'Nothing today', s: 'scripts appear here as he writes them and you decide them', tc: M })] }),
    card('What Sawyer is drawing on now', { wide: true, src: 'composed from: T1 step → T2 → deep expertise → season vision → shared memory', items: drawItems.length ? drawItems : [item({ t: 'Nothing yet', s: 'he has not walked a step since the server started', tc: M })] }),
  ];
}

export function seasonPlanCards(board: Board): Card[] {
  const sp = board.season_plan;
  const sid = sp.season_id || board.season_id || 'S—';
  const circle: Row[] = sp.present ? sp.progress.map((p) => {
    const eps = p.episodes || [];
    const range = eps.length === 0 ? '[episodes]' : eps.length === 1 ? eps[0].replace(/^S\d+/, '') : `${eps[0].replace(/^S\d+/, '')}–${eps[eps.length - 1].replace(/^S\d+/, '')}`;
    const t = p.tally || {};
    const active = !!(t['at gate'] || t['writing'] || t['drafting'] || t['kicked back']);
    return { k: `${p.step ?? ''} · ${p.name ?? ''}`, v: eps.includes(sp.finale_episode_id || '') ? `${range} · finale` : range, c: active ? A : W };
  }) : [{ k: 'No season plan yet', v: 'written at the season start, from your season vision', c: M }];
  const settings: Row[] = (sp.settings || []).filter((r: any) => r && r.key).map((r: any) => ({ k: String(r.key), v: String(r.value ?? ''), c: W }));
  return [
    card(`${sid} · story circle · one step a notch`, { src: "season format from deep expertise · built from Matt's season vision", rows: circle }),
    card('Season settings', { src: "from Matt's season vision", rows: settings.length ? settings : [{ k: 'No settings yet', v: 'taken from your season vision', c: M }] }),
  ];
}

export function outlineCards(board: Board): Card[] {
  const items = board.outlines.map((o) => {
    const st = o.state;
    const [tag, tc] = !st || st === 'drafting' ? ['DRAFTING', A] : st === 'writing' ? ['IN SCRIPT', B] : st === 'at gate' ? ['AT GATE', A] : st === 'passed' ? ['PASSED', G] : ['KICKED BACK', R];
    const s = st === 'writing' ? `outline done${o.pays_off ? ' · pays off ' + o.pays_off : ''} · now in Scripts` : [o.encounter, o.reversal, o.cliffhanger_type].filter(Boolean).join(' · ') || o.summary || '';
    return item({ t: `${o.episode_id} · ${o.title || ''}`, s, tag, tc });
  });
  return [card('Outlines in flight', { src: 'Sawyer T2 · outlines · no gate on outlines', items: items.length ? items : [item({ t: 'No outlines yet', s: 'the next planned episode is outlined when the loop reaches it', tc: M })] })];
}

export function scriptCards(board: Board): Card[] {
  const writing = board.scripts.filter((s) => s.state === 'written');
  const s = writing[writing.length - 1] || board.scripts[board.scripts.length - 1];
  const trace = s ? board.traces[s.episode_id] || [] : [];
  const word = s ? ({ written: 'writing', at_gate: 'at gate', passed: 'passed', kicked_back: 'kicked back' } as Record<string, string>)[s.state || ''] || s.state : '';
  return [
    s ? card(`${s.episode_id} · v${s.version ?? 1} · ${word}`, { wide: true, aside: 'A = inner voice · B = talking, no exact sync', src: 'Sawyer T2 · scripts · format from deep expertise', doc: docLines(s.document) })
      : card('No script yet', { wide: true, aside: 'A = inner voice · B = talking, no exact sync', src: 'Sawyer T2 · scripts · format from deep expertise', items: [item({ t: 'Nothing written yet', s: 'the first script is written once the season has a plan and an outline', tc: M })] }),
    card('Framework trace', { src: 'every kick-back traces to the rule that produced it', rows: trace.length ? trace.map((t) => ({ k: String(t.beat || ''), v: String(t.rule || ''), c: W })) : [{ k: 'No trace yet', v: 'written with each script', c: M }] }),
  ];
}

export function journeyCards(board: Board): Card[] {
  const sid = board.season_id;
  const sums = board.journeys.season_summaries.map((s) => item({ t: `Season ${String(s.season_id || '').replace(/^S0?/, '')}`, s: String(s.summary || ''), tag: s.season_id || '', tc: B }));
  if (sid && !board.journeys.season_summaries.some((s) => s.season_id === sid)) sums.push(item({ t: `Season ${sid.replace(/^S0?/, '')}`, s: 'in progress — written after the finale', tag: sid, tc: M }));
  const shared = 'domain shared memory · kept by Hammond (Domain CEO) after each episode goes out · read-only here';
  const chars: Card[] = board.journeys.characters.map((c) => card(`Character · ${c.name || c.subject || ''}`, {
    src: shared,
    items: [
      c.arc ? item({ t: 'Arc so far', s: String(c.arc) }) : null,
      c.repeated_threads ? item({ t: 'Repeated threads', s: Array.isArray(c.repeated_threads) ? c.repeated_threads.join(' · ') : String(c.repeated_threads) }) : null,
      c.latest_update ? item({ t: 'Latest update', s: String(c.latest_update), tag: String(c.latest_episode || ''), tc: B }) : null,
    ].filter(Boolean) as Item[],
  }));
  const rels = board.journeys.relationships.map((r) => item({ t: String(r.name || r.subject || ''), s: String(r.summary || r.kind || ''), tag: String(r.since || ''), tc: B }));
  return [
    card('Season summaries', { src: 'Sawyer T2 · journeys · one per season, written once the finale passes', items: sums.length ? sums : [item({ t: 'No season yet', s: 'a season starts when you send its vision from Home', tc: M })] }),
    ...(chars.length ? chars : [card('Characters', { src: shared, items: [item({ t: 'No character files yet', s: 'Hammond keeps each character\'s file in the domain\'s shared memory — later, in his build', tc: M })] })]),
    card('Relationships', { src: shared, items: rels.length ? rels : [item({ t: 'No relationship files yet', s: 'Hammond keeps each relationship\'s folder — later, in his build', tc: M })] }),
  ];
}

export function kickCards(board: Board, h: Hooks): Card[] {
  const rows = kickRows(board);
  const bars = gapBars(board);
  return [
    card('Kick-backs', {
      src: 'Sawyer T2 · kick-backs · each chat saved with the rule it maps to',
      items: rows.length ? rows.map((k) => item({ t: k.t, s: k.dir, tag: k.tag, tc: k.tc, meta: k.state + (k.kind ? ` · ${k.kind}` : ''), link: h.openKick[k.id] ? 'Close chat' : 'Open chat', go: () => h.toggleKick(k.id), thread: h.openKick[k.id] ? k.chat : [] }))
        : [item({ t: 'No kick-backs', s: 'a script you kick back at Gate 1 shows here with its chat', tc: M })],
    }),
    card('Gaps', { aside: `threshold ${board.gap_at || 10}`, src: "at 10 a recurring kick-back becomes a gap · Matt fixes it in Sawyer's instructions", bars, items: bars.length ? undefined : [item({ t: 'No repeats yet', s: 'each kind of kick-back is counted here', tc: M })] }),
  ];
}

export function expertiseCards(board: Board): Card[] {
  const loaded = board.expertise.filter((e) => e.state === 'loaded').length;
  const ph = board.expertise.filter((e) => e.state === 'placeholder').length;
  const missing = board.expertise.length - loaded - ph;
  return [card('Deep expertise · craft library', {
    aside: `${loaded} loaded · ${ph} placeholder${missing ? ` · ${missing} not uploaded` : ''}`,
    src: 'back-end T1 · uploaded by Matt · read-only',
    rows: board.expertise.map((e) => ({ k: e.file, v: e.state === 'loaded' ? 'loaded' : e.state, c: e.state === 'loaded' ? G : e.state === 'placeholder' ? M : R })),
  })];
}

export function cardsFor(tab: number, board: Board, loopOn: boolean | null, h: Hooks): Card[] {
  switch (tab) {
    case 0: return homeCards(board, loopOn, h);
    case 1: return todayCards(board);
    case 2: return seasonPlanCards(board);
    case 3: return outlineCards(board);
    case 4: return scriptCards(board);
    case 5: return journeyCards(board);
    case 6: return kickCards(board, h);
    case 7: return expertiseCards(board);
    default: return [];
  }
}

export function scriptForGate(board: Board, eid: string) {
  return scriptOf(board, eid);
}
